'use client';

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { queryKeys, DEALS_VIEW_KEY, DEALS_BY_BOARD_KEY } from '../queryKeys';
import { dealsViewQueryFn, dealsByBoardQueryFn } from './useDealsQuery';
import { boardsService, contactsService, companiesService, activitiesService, dealsService } from '@/lib/supabase';

const STALE_30S  = 30  * 1000;
const STALE_2MIN =  2  * 60 * 1000;
const STALE_5MIN =  5  * 60 * 1000;

type PrefetchEntry = {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
  staleTime: number;
};

/**
 * Lê o board ativo do localStorage (mesma chave usada em useBoardsController).
 * Retorna null no SSR ou se não houver board selecionado.
 */
function readActiveBoardId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('crm_active_board_id');
  } catch {
    return null;
  }
}

/**
 * Mapa rota → queries que devem ser pré-aquecidas.
 *
 * Regra: queryKey e staleTime devem ser idênticos aos dos hooks reais,
 * para que o dado prefetchado seja aproveitado sem re-fetch ao montar.
 *
 * `/boards` é dinâmico: prefetcha o board atualmente ativo (lendo localStorage).
 * Se não houver board ativo, só prefetcha a lista de boards.
 */
function getBoardsPrefetchEntries(): PrefetchEntry[] {
  const entries: PrefetchEntry[] = [
    {
      queryKey: queryKeys.boards.lists(),
      queryFn: async () => {
        const { data, error } = await boardsService.getAll();
        if (error) throw error;
        return data ?? [];
      },
      staleTime: STALE_5MIN,
    },
  ];

  const activeBoardId = readActiveBoardId();
  if (activeBoardId && !activeBoardId.startsWith('temp-')) {
    entries.push({
      queryKey: DEALS_BY_BOARD_KEY(activeBoardId),
      queryFn: () => dealsByBoardQueryFn(activeBoardId),
      staleTime: STALE_2MIN,
    });
  }

  return entries;
}

const ROUTE_PREFETCH: Readonly<Record<string, PrefetchEntry[] | (() => PrefetchEntry[])>> = {
  '/boards': getBoardsPrefetchEntries,

  '/contacts': [
    {
      queryKey: queryKeys.contacts.lists(),
      queryFn: async () => {
        const { data, error } = await contactsService.getAll();
        if (error) throw error;
        return data ?? [];
      },
      staleTime: STALE_2MIN,
    },
    {
      queryKey: queryKeys.companies.lists(),
      queryFn: async () => {
        const { data, error } = await companiesService.getAll();
        if (error) throw error;
        return data ?? [];
      },
      staleTime: STALE_5MIN,
    },
  ],

  '/activities': [
    {
      queryKey: queryKeys.activities.lists(),
      queryFn: async () => {
        const { data, error } = await activitiesService.getAll();
        if (error) throw error;
        return data ?? [];
      },
      staleTime: STALE_30S,
    },
    {
      // useActivitiesController usa useDeals() → queryKeys.deals.lists() + dealsService.getAll()
      queryKey: queryKeys.deals.lists(),
      queryFn: async () => {
        const { data, error } = await dealsService.getAll({});
        if (error) throw error;
        return data ?? [];
      },
      staleTime: STALE_2MIN,
    },
  ],

  '/dashboard': [
    {
      queryKey: DEALS_VIEW_KEY,
      queryFn: () => dealsViewQueryFn(),
      staleTime: STALE_2MIN,
    },
    {
      queryKey: queryKeys.boards.lists(),
      queryFn: async () => {
        const { data, error } = await boardsService.getAll();
        if (error) throw error;
        return data ?? [];
      },
      staleTime: STALE_5MIN,
    },
  ],
};

/**
 * Hook que retorna uma função `prefetch(href)` estável.
 *
 * Chame no `onMouseEnter` dos links de navegação para pré-aquecer o cache
 * antes do usuário clicar. TanStack Query ignora o prefetch automaticamente
 * se os dados já estiverem dentro do staleTime.
 *
 * @example
 * ```tsx
 * const prefetch = usePrefetchRoute();
 * <Link href="/boards" onMouseEnter={() => prefetch('/boards')}>...</Link>
 * ```
 */
export function usePrefetchRoute() {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  return useCallback(
    (href: string) => {
      // Não prefetch sem auth — as queries dependem de RLS do Supabase
      if (authLoading || !user) return;

      const entry = ROUTE_PREFETCH[href];
      if (!entry) return;

      const entries = typeof entry === 'function' ? entry() : entry;

      for (const { queryKey, queryFn, staleTime } of entries) {
        queryClient.prefetchQuery({ queryKey, queryFn, staleTime });
      }
    },
    [queryClient, user, authLoading]
  );
}
